import type { SheetType } from '../models/sheet-types';

/** SPA route for the multi-sheet filing form (matches the xbrl-files reference link). */
export const FILING_FORM_ROUTE = ['/mbrs/filing'] as const;

/**
 * Query-param key carrying the field to scroll to on the destination sheet.
 * Shared by the source anchor (writes it) and the destination sheets (read it),
 * so the two sides can never drift on the literal.
 */
export const FIELD_LINK_SCROLL_PARAM = 'scrollTo';

/**
 * Role-code -> filing-form sheet route value. Single source of truth, replacing
 * the per-component `getRoleSheetMapping` copies (which had drifted on a few
 * never-navigated entries, e.g. 110000). Only 210100 / 310100 / 410000 are
 * reachable by live schemas today; the rest are kept for completeness and match
 * the role12 reference component's map. '0'/'1'/'2' map to the filing-info /
 * scope-of-filing sheet ROUTE values (note: role13's map keyed those to the
 * filingPrerequisites/filingInfo state keys, which are distinct from route
 * values — both filing phases render under the single 'filing-info' route).
 */
const ROLE_TO_SHEET: Readonly<Record<string, SheetType>> = {
  '0': 'filing-info',
  '1': 'filing-info',
  '2': 'scope-of-filing',
  '010000': 'filing-info',
  '020000': 'scope-of-filing',
  '110000': 'stock-exchange',
  '120000': 'directors-report',
  '120100': 'statement-by-directors',
  '120200': 'director-business-review',
  '130000': 'auditors-report',
  '210000': 'statement-of-financial-position',
  '210100': 'sub-classification',
  '220000': 'statement-of-financial-position-liquidity',
  '220100': 'sub-classification-liquidity',
  '310000': 'statement-of-profit-or-loss',
  '310100': 'analysis-of-profit-or-loss',
  '320000': 'statement-of-profit-or-loss-by-nature',
  '320100': 'analysis-of-profit-or-loss-by-nature',
  '410000': 'statement-of-comprehensive-income',
  '420000': 'statement-of-comprehensive-income-before-tax',
  '510000': 'statement-of-cash-flows-direct',
  '520000': 'statement-of-cash-flows-indirect',
  '610000': 'statement-of-changes-in-equity',
  '710000': 'notes-corporate-information',
  '720000': 'notes-accounting-policies',
  '730000': 'notes-list-of-notes',
  '740000': 'notes-issued-capital',
  '750000': 'notes-related-party-transactions',
};

/** UI link metadata read off a schema control (`control.ui`). */
export interface FieldLinkUi {
  linkTo?: {
    role?: string;
    qname?: string;
    bidirectional?: boolean;
    /**
     * Opt OUT of navigation while still carrying the VALUE. Absent (the normal
     * case) means navigate — only an explicit `false` suppresses.
     *
     * Role 15's `Profit (loss)` is the one link declared this way: the P.O.
     * asked for the value to track Role 12 now and for the row-label hyperlink
     * "much later, separate pass". Because inheritance is keyed on the raw
     * `ui.linkTo.role` (`applyLinkedInheritance`, `pullRole12Values`) and every
     * navigation surface funnels through `resolveFieldLinkTarget`, honouring
     * the flag HERE separates the two cleanly: the anchor, the tooltip, and the
     * reverse-index back-link edge all disappear, and the value still moves.
     */
    navigate?: boolean;
  } | null;
  inheritFromRole?: string | null;
  inheritFromRole9?: boolean | null;
}

export interface FieldLinkTarget {
  /** Route value of the destination sheet — a `SheetType`, so callers can index
   *  `SHEET_DISPLAY_NAMES` with it without a cast (see `fieldLinkTooltipModel`). */
  sheet: SheetType;
  scrollTo: string;
}

/**
 * Extract the numeric role code from any of the legacy formats:
 *   '210100' -> '210100', 'Role9[210100]' -> '210100' (bracketed wins over the
 *   leading '9'), 'role-0' / 'Role-2' -> '0' / '2'.
 */
function normalizeRoleCode(roleCode: string): string {
  const bracket = roleCode.match(/\[(\d+)\]/);
  if (bracket)
    return bracket[1];

  const stripped = roleCode.replace(/role-/i, '');
  const digits = stripped.match(/(\d+)/);

  return digits ? digits[1] : roleCode;
}

// ROLE_TO_SHEET is intentionally module-private — callers go through
// resolveFieldLinkTarget. Bare 'Role9' (no brackets, no hyphen) is not a known
// schema input; it would normalize to '9', which is deliberately absent from the map.
function roleToSheet(roleCode: string): SheetType | null {
  return ROLE_TO_SHEET[normalizeRoleCode(roleCode)] ?? null;
}

/**
 * Resolve a control's link metadata into a navigation target, or null when the
 * control has no resolvable link (caller renders plain text instead of a dead
 * anchor). Precedence matches the legacy navigateToSourceRole: linkTo, then
 * inheritFromRole9, then generic inheritFromRole.
 *
 * A `linkTo` carrying `navigate: false` is value-only and resolves to null —
 * see the flag's docblock on {@link FieldLinkUi}.
 */
export function resolveFieldLinkTarget(ui: FieldLinkUi | null | undefined, ownQname: string): FieldLinkTarget | null {
  if (!ui)
    return null;

  if (ui.linkTo?.navigate === false)
    return null;

  if (ui.linkTo?.role && ui.linkTo?.qname) {
    const sheet = roleToSheet(ui.linkTo.role);
    return sheet ? { sheet, scrollTo: ui.linkTo.qname } : null;
  }

  if (ui.inheritFromRole9)
    return { sheet: 'sub-classification', scrollTo: ownQname };

  if (ui.inheritFromRole) {
    const sheet = roleToSheet(ui.inheritFromRole);
    return sheet ? { sheet, scrollTo: ownQname } : null;
  }

  return null;
}

/**
 * Build the `[queryParams]` object for the field-link anchor, or null when the
 * control is not linkable. Use with `queryParamsHandling="merge"` so id /
 * companyName / fileName from the current URL are preserved.
 */
export function fieldLinkQueryParams(ui: FieldLinkUi | null | undefined, ownQname: string): FieldLinkTarget | null {
  const target = resolveFieldLinkTarget(ui, ownQname);
  return target ? { sheet: target.sheet, [FIELD_LINK_SCROLL_PARAM]: target.scrollTo } : null;
}
